import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import {
  Plug,
  Check,
  X,
  Loader2,
  AlertCircle,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useIntegrations } from '@/hooks/useIntegrations';

const integrationProviders = [
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'Auto-create contacts from vCard QR scans',
    logo: '🧡',
    color: 'bg-orange-100 text-orange-700',
    available: true,
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Sync leads and opportunities',
    logo: '☁️',
    color: 'bg-blue-100 text-blue-700',
    available: true,
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    description: 'Add scanners to email lists',
    logo: '🐵',
    color: 'bg-yellow-100 text-yellow-700',
    available: false,
  },
  {
    id: 'google_sheets',
    name: 'Google Sheets',
    description: 'Export analytics to spreadsheets',
    logo: '📊',
    color: 'bg-green-100 text-green-700',
    available: false,
  },
];

export function IntegrationsSection() {
  const { integrations, isLoading, connectIntegration, disconnectIntegration, toggleIntegration } =
    useIntegrations();
  const [expandedLogs, setExpandedLogs] = useState<string | null>(null);

  const getIntegrationStatus = (providerId: string) => {
    return integrations.find((i) => i.provider === providerId);
  };

  const handleConnect = async (providerId: string) => {
    if (providerId === 'hubspot' || providerId === 'salesforce') {
      await connectIntegration.mutateAsync(providerId);
    }
  };

  const handleDisconnect = async (providerId: string) => {
    if (confirm('Are you sure you want to disconnect this integration?')) {
      await disconnectIntegration.mutateAsync(providerId);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5 text-primary" />
            Integrations
          </CardTitle>
          <CardDescription>
            Connect NexQR with your favorite tools to automate workflows
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <AnimatePresence>
              {integrationProviders.map((provider) => {
                const integration = getIntegrationStatus(provider.id);
                const isConnected = !!integration;

                return (
                  <motion.div
                    key={provider.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div
                          className={`p-3 rounded-lg text-2xl ${provider.color}`}
                        >
                          {provider.logo}
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{provider.name}</h3>
                            {isConnected && (
                              <Badge variant="secondary" className="text-xs h-5">
                                <Check className="h-3 w-3 mr-1" />
                                Connected
                              </Badge>
                            )}
                            {!provider.available && (
                              <Badge variant="outline" className="text-xs h-5">
                                Coming Soon
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {provider.description}
                          </p>
                          {integration?.last_sync_at && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Activity className="h-3 w-3" />
                              Last synced: {format(new Date(integration.last_sync_at), 'PPp')}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {isConnected && (
                          <Switch
                            checked={integration.is_active}
                            onCheckedChange={(checked) =>
                              toggleIntegration.mutate({ id: integration.id, isActive: checked })
                            }
                          />
                        )}

                        {isConnected ? (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDisconnect(provider.id)}
                            disabled={disconnectIntegration.isPending}
                          >
                            {disconnectIntegration.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <X className="h-4 w-4 mr-2" />
                                Disconnect
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleConnect(provider.id)}
                            disabled={!provider.available || connectIntegration.isPending}
                          >
                            {connectIntegration.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Plug className="h-4 w-4 mr-2" />
                                Connect
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Show integration details if connected */}
                    {isConnected && integration.metadata && (integration.metadata.hub_domain as string) && (
                      <div className="mt-4 pt-4 border-t space-y-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Account:</span>
                          <span className="ml-2 font-mono">
                            {String(integration.metadata.hub_domain)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Connected:</span>
                          <span className="ml-2">
                            {format(new Date(integration.created_at), 'PPp')}
                          </span>
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">How Integrations Work</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 shrink-0 text-blue-500" />
            <div>
              <p className="font-medium text-foreground">Automatic Syncing</p>
              <p>When someone scans your vCard QR code, their contact info is automatically synced to your connected CRM.</p>
            </div>
          </div>
          <Separator />
          <div className="flex gap-3">
            <Check className="h-5 w-5 shrink-0 text-green-500" />
            <div>
              <p className="font-medium text-foreground">Real-time Updates</p>
              <p>Changes are synced in real-time, so your CRM is always up to date with the latest scan data.</p>
            </div>
          </div>
          <Separator />
          <div className="flex gap-3">
            <Activity className="h-5 w-5 shrink-0 text-purple-500" />
            <div>
              <p className="font-medium text-foreground">Activity Logs</p>
              <p>Track all sync events and troubleshoot any issues with detailed logs.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
